import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { ProTemplateId, ProTemplate, templates, defaultTemplateId } from '@/config/templates';

interface TemplateContextValue {
  templateId: ProTemplateId;
  template: ProTemplate;
  setTemplateId: (id: ProTemplateId) => void;
  isTransitioning: boolean;
  previewMode: boolean;
  setPreviewMode: (mode: boolean) => void;
}

const TemplateContext = createContext<TemplateContextValue | undefined>(undefined);

export const TemplateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [templateId, setTemplateIdState] = useState<ProTemplateId>(defaultTemplateId);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  const template = templates[templateId];

  // Apply CSS custom properties when template changes
  useEffect(() => {
    const root = document.documentElement;
    const colors = template.colors;
    
    root.style.setProperty('--primary', colors.primary);
    root.style.setProperty('--primary-foreground', colors.primaryForeground);
    root.style.setProperty('--secondary', colors.secondary);
    root.style.setProperty('--secondary-foreground', colors.secondaryForeground);
    root.style.setProperty('--accent', colors.accent);
    root.style.setProperty('--accent-foreground', colors.accentForeground);
    root.style.setProperty('--background', colors.background);
    root.style.setProperty('--foreground', colors.foreground);
    root.style.setProperty('--muted', colors.muted);
    root.style.setProperty('--muted-foreground', colors.mutedForeground);
    root.style.setProperty('--card', colors.card);
    root.style.setProperty('--card-foreground', colors.cardForeground);
    root.style.setProperty('--border', colors.border);
    
    // Also set font families
    root.style.setProperty('--font-heading', template.fonts.heading);
    root.style.setProperty('--font-body', template.fonts.body);
  }, [template]);

  const setTemplateId = useCallback((id: ProTemplateId) => {
    if (id === templateId) return;
    
    setIsTransitioning(true);
    
    // Small delay for transition effect
    setTimeout(() => {
      setTemplateIdState(id);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 300);
    }, 150);
  }, [templateId]);

  return (
    <TemplateContext.Provider value={{ 
      templateId, 
      template, 
      setTemplateId, 
      isTransitioning,
      previewMode,
      setPreviewMode,
    }}>
      {children}
    </TemplateContext.Provider>
  );
};

export const useTemplate = () => {
  const ctx = useContext(TemplateContext);
  if (!ctx) throw new Error('useTemplate must be used within TemplateProvider');
  return ctx;
};
